import type { Routes } from '@angular/router';
import { guards } from '@app/app.guards';
import { backendUnavailableGuard } from '@core/guards/backend-unavailable.guard';
import { checkMaintenanceGuard } from '@core/guards/maintenance.guard';
import { redirectGuard } from '@core/guards/redirect.guard';
import { AppPathsMap } from '@core/routing/app-paths';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { defineRoute, IconNameMap } from '@kerberos-compliance/compliance-shared-ui';
import { roleGuard } from '@/auth/guard/role.guard';
import { contractActiveGuard } from '@/contract/guards/contract.guard';
import { denyActiveContractGuard } from '@/contract/guards/deny-active-contract.guard';

const allCustomerRoles = [
  NamedRoleMap.BasicUser,
  NamedRoleMap.RiskAnalysisCustomer,
  NamedRoleMap.LocationAnalysisUser,
  NamedRoleMap.WorkItemUser,
  NamedRoleMap.KycRiskEvaluationApiUser,
  NamedRoleMap.AcademyUser,
  NamedRoleMap.AcademyCoordinator,
];

const featureRoutes: Routes = [
  defineRoute({
    key: 'home',
    path: AppPathsMap.Home,
    loadChildren: () => import('@features/home/home.routes').then((m) => m.homeRoutes),
    canActivate: guards,
    data: {
      nav: { labelKey: 'navigation.dashboard', icon: IconNameMap.home, order: 0 },
    },
  }),
  defineRoute({
    key: 'dashboard',
    path: AppPathsMap.Dashboard,
    loadChildren: () => import('@risk-analysis/risk-analysis.routes').then((m) => m.dashboardRoutes),
    canActivate: [...guards, roleGuard, contractActiveGuard],
    data: {
      roles: [NamedRoleMap.RiskAnalysisCustomer],
    },
  }),
  defineRoute({
    key: 'riskAnalysis',
    path: AppPathsMap.RiskAnalysis.Root,
    loadChildren: () => import('@risk-analysis/risk-analysis.routes').then((m) => m.riskAnalysisRoutes),
    canActivate: [...guards, roleGuard, contractActiveGuard],
    data: {
      roles: [NamedRoleMap.RiskAnalysisCustomer],
      nav: { labelKey: 'navigation.riskAnalysis', icon: IconNameMap.bookOpenLines, order: 2 },
    },
  }),
  defineRoute({
    key: 'transparencyRegister',
    path: AppPathsMap.TransparencyRegister,
    loadChildren: () =>
      import('@features/transparency-register/transparency-register.routes').then((m) => m.transparencyRegisterRoutes),
    canActivate: [...guards, roleGuard],
    data: {
      roles: [NamedRoleMap.RiskAnalysisCustomer],
      nav: { labelKey: 'navigation.transparency', icon: IconNameMap.fileCertificate, order: 1 },
    },
  }),
  defineRoute({
    key: 'kyc',
    path: AppPathsMap.Kyc.Root,
    loadChildren: () => import('@features/work-item/kyc/kyc.routes').then((m) => m.kycRoutes),
    canActivate: [...guards, roleGuard],
    data: {
      roles: [NamedRoleMap.WorkItemUser, NamedRoleMap.KycRiskEvaluationApiUser],
      nav: { labelKey: 'kyc.navigation.introduction', icon: IconNameMap.myBusinessPartners, order: 3 },
    },
  }),
  // Backward compatibility routes for old URLs
  {
    path: AppPathsMap.Legacy.Kyc.Root,
    redirectTo: AppPathsMap.Kyc.Root,
  },
  defineRoute({
    key: 'locationAnalysis',
    path: AppPathsMap.LocationAnalysis.Root,
    loadChildren: () => import('@features/location-analysis/la.routes').then((m) => m.laRoutes),
    canActivate: [...guards, roleGuard],
    data: {
      roles: [NamedRoleMap.LocationAnalysisUser],
      nav: { labelKey: 'locationAnalysis.navigation.introduction', icon: IconNameMap.locationDot, order: 5 },
    },
  }),
  defineRoute({
    key: 'training',
    path: AppPathsMap.Academy.Root,
    loadChildren: () => import('@features/academy/academy-routes').then((m) => m.academyRoutes),
    canActivate: [...guards, roleGuard],
    data: {
      roles: [NamedRoleMap.AcademyUser, NamedRoleMap.AcademyCoordinator],
      nav: { labelKey: 'navigation.training', icon: IconNameMap.graduationCap, order: 4 },
    },
  }),
  defineRoute({
    key: 'contact',
    path: AppPathsMap.Contact,
    loadChildren: () => import('@features/contact/contact.routes').then((m) => m.contactRoutes),
    canActivate: guards,
    data: {
      nav: { labelKey: 'navigation.contact', icon: IconNameMap.envelope, order: 6 },
    },
  }),
  defineRoute({
    key: 'profile',
    path: AppPathsMap.Profile,
    loadComponent: () =>
      import('@features/user/pages/profile-page/profile-page.component').then((m) => m.ProfilePageComponent),
    canActivate: guards,
  }),
  defineRoute({
    key: 'contractExpired',
    path: AppPathsMap.ContractExpired,
    loadChildren: () => import('@/contract/contract.routes').then((m) => m.contractRoutes),
    canActivate: [...guards, roleGuard, denyActiveContractGuard],
    data: {
      roles: [NamedRoleMap.BasicUser, NamedRoleMap.RiskAnalysisCustomer],
    },
  }),
  defineRoute({
    key: 'faq',
    path: AppPathsMap.Faq.Root,
    loadChildren: () => import('@features/faq/faq.routes').then((m) => m.faqRoutes),
    canActivate: [...guards, roleGuard],
    data: {
      roles: allCustomerRoles,
      nav: { labelKey: 'navigation.frequentlyAskedQuestions', icon: IconNameMap.lightbulb, order: 7 },
    },
  }),
  defineRoute({
    key: 'documentLibrary',
    path: AppPathsMap.DocumentLibrary,
    loadChildren: () =>
      import('@features/document-library/document-library.routes').then((m) => m.documentLibraryRoutes),
    canActivate: [...guards, roleGuard],
    data: {
      roles: allCustomerRoles,
      nav: { labelKey: 'navigation.documentLibrary', icon: IconNameMap.folderOpen, order: 8 },
    },
  }),
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () => import('@core/pages/redirect.component').then((m) => m.RedirectComponent),
    canActivate: [roleGuard],
    data: {
      enableRedirect: true,
    },
  },
];

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('@core/components/layout/layout.component').then((m) => m.LayoutComponent),
    canActivateChild: [checkMaintenanceGuard],
    children: featureRoutes,
  },
  defineRoute({
    key: 'logout',
    path: AppPathsMap.Logout,
    loadComponent: () => import('@/auth/pages/logout-page/logout-page.component').then((m) => m.LogoutPageComponent),
  }),
  defineRoute({
    key: 'login',
    path: AppPathsMap.Login,
    loadComponent: () => import('@/auth/pages/login-page/login-page.component').then((m) => m.LoginPageComponent),
  }),
  defineRoute({
    key: 'unauthorized',
    path: AppPathsMap.Error.Unauthorized,
    loadComponent: () =>
      import('@core/pages/error-pages/unauthorized/unauthorized-page.component').then(
        (m) => m.UnauthorizedPageComponent,
      ),
  }),
  defineRoute({
    key: 'maintenance',
    path: AppPathsMap.Error.Maintenance,
    canActivate: [checkMaintenanceGuard],
    loadComponent: () =>
      import('@core/pages/error-pages/maintenance-page/maintenance-page.component').then(
        (m) => m.MaintenancePageComponent,
      ),
  }),
  defineRoute({
    key: 'backendUnavailable',
    path: AppPathsMap.Error.BackendUnavailable,
    canActivate: [backendUnavailableGuard],
    loadComponent: () =>
      import('@core/pages/error-pages/backend-unavailable/backend-unavailable-page.component').then(
        (m) => m.BackendUnavailablePageComponent,
      ),
  }),
  defineRoute({
    key: 'userNotActive',
    path: AppPathsMap.Error.UserNotActive,
    loadComponent: () =>
      import('@core/pages/error-pages/user-not-active/user-not-active-page.component').then(
        (m) => m.UserNotActivePageComponent,
      ),
  }),
  defineRoute({
    key: 'noGrants',
    path: AppPathsMap.Error.NoGrants,
    loadComponent: () =>
      import('@core/pages/error-pages/no-grants/no-grants-page.component').then((m) => m.NoGrantsPageComponent),
  }),
  defineRoute({
    key: 'grantLoadError',
    path: AppPathsMap.Error.GrantLoadError,
    loadComponent: () =>
      import('@core/pages/error-pages/grant-load-error/grant-load-error-page.component').then(
        (m) => m.GrantLoadErrorPageComponent,
      ),
  }),
  defineRoute({
    key: 'loginError',
    path: AppPathsMap.Error.LoginError,
    loadComponent: () =>
      import('@core/pages/error-pages/login-error/login-error-page.component').then((m) => m.LoginErrorPageComponent),
  }),
  defineRoute({
    key: 'sessionUnavailable',
    path: AppPathsMap.Error.SessionUnavailable,
    loadComponent: () =>
      import('@core/pages/error-pages/session-unavailable/session-unavailable-page.component').then(
        (m) => m.SessionUnavailablePageComponent,
      ),
  }),
  defineRoute({
    key: 'notFound',
    path: AppPathsMap.Error.NotFound,
    loadComponent: () =>
      import('@core/pages/error-pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
  }),
  {
    path: '**',
    pathMatch: 'full',
    loadComponent: () => import('@core/pages/redirect.component').then((m) => m.RedirectComponent),
    canActivate: [redirectGuard],
  },
];
